<script setup lang="ts">
import BasicInfoCard from '../basic-info-card.vue';
import BasicValueCard from '../basic-value-card.vue';
import EnergyInfoCard from '../energy-info-card.vue';
import EventTable from '../event-table.vue';
import InvertCard from '../invert-card.vue';
import LinkageInfoCard from '../linkage-info-card.vue';
import ManagerInfo from '../manager-info.vue';
import OwnerInfo from '../owner-info.vue';
import ProdChart from '../prod-chart.vue';
import RoundChart from '../round-chart.vue';
import RtuInfo from '../rtu-info.vue';
import SchematicCard from '../schematic-card.vue';
import SimpleEquipStatus from '../simple-equip-status.vue';
import TotalProdCard from '../total-prod-card.vue';
import BaseComponent from '../base-component.vue';
import WeatherInfo from '../weather-info-card.vue';
import InfoTap from '../info-tap.vue';
import SpecInfo from '../spec-info.vue';
import InstallInfo from '../install-info.vue';
import InvertCardList from '../invert-card-list.vue';
import WeatherProdChart from '../weather-prod.chart.vue';
import GaugeChart from '../gauge-chart.vue';
import CurtailmentCard from '../curtailment-card.vue';
import { useTocStore } from '~/store/tocStore';
import { useEquipStore } from '~/store/equipStore';
import { useUserStore } from '~/store/userStore';
import { UserAuthGroup } from '~/enums';
import RawdataChart from '../rawdata-chart.vue';
import RawdataCard from '../rawdata-card.vue';
import MapRender from '../map-render.vue';
import DateChart from '../date-chart.vue';
import GeothermalProdUsageChart from '../geothermal-prod-usage-chart.vue';
import GeothermalSchematicCard from '../geothermal-schematic-card.vue';
import EditableImageCard from '../editable-image-card.vue';
import SystemStatusCard from '../system-status-card.vue';
import BaseTable from '../base-table.vue';
import moment from 'moment';

const tocStore = useTocStore();
const equipStore = useEquipStore();
const userStore = useUserStore();

const refreshInterval = ref();

// 그리드 시스템 설정 (12, 24, 36 중 선택)
// default.json의 위젯 설정에서 gridSystem 필드로 설정 가능
// 기본값은 12 (기존 Bootstrap 그리드)
const gridColumns = ref(12);

const props = defineProps<{
  widgets: any[],
  pageType: string,
  gridSystem?: number  // 그리드 시스템 (12, 24, 36) - 선택사항, 기본값 12
}>();

// BaseComponent 참조들을 저장할 Map
const baseComponentRefs = ref<Map<string, any>>(new Map());

// MapRender의 동적 파라미터 상태
const mapRenderParams = ref<{ parentDongCode?: string | null, groupLevel?: string | null }>({
  parentDongCode: null,
  groupLevel: null
});

// API 호출 제어 상태 - 기본적으로 활성화
const isAllApiEnabled = ref(true);

// 페이지 새로고침 시 전역 로딩 상태
const isGlobalLoading = ref(true);

// 전체 로딩 상태 계산
const isAnyWidgetLoading = computed(() => isGlobalLoading.value);

// BaseComponent 참조 등록
const registerBaseComponent = (id: string, ref: any) => {
  baseComponentRefs.value.set(id, ref);
  
  // 컴포넌트 등록 시 즉시 API 호출 시작 (비동기)
  nextTick(() => {
    if (ref && ref.callApi && isAllApiEnabled.value) {
      ref.callApi();
    }
  });
};

// BaseComponent 참조 해제
const unregisterBaseComponent = (id: string) => {
  baseComponentRefs.value.delete(id);
};

// 컴포넌트 마운트 시 초기화
onMounted(() => {
  // 그리드 시스템 설정 확인
  // 1. props에서 gridSystem이 전달되면 사용
  // 2. 위젯 설정의 첫 번째 위젯에 gridSystem이 있으면 사용
  // 3. 기본값은 12 (Bootstrap 기본 그리드)
  if (props.gridSystem && [12, 24, 36].includes(props.gridSystem)) {
    gridColumns.value = props.gridSystem;
  } else if (props.widgets && props.widgets.length > 0) {
    const firstWidget = props.widgets[0];
    if (firstWidget.gridSystem && [12, 24, 36].includes(firstWidget.gridSystem)) {
      gridColumns.value = firstWidget.gridSystem;
    }
  }
  
  // 페이지 새로고침 시 전역 로딩 상태 활성화
  isGlobalLoading.value = true;
  
  // 모든 위젯이 로드될 때까지 대기 (실제로는 API 응답 시간에 따라 조정)
  setTimeout(() => {
    isGlobalLoading.value = false;
  }, 2000);


  let nowTime = moment();

  let laterTime = moment(nowTime).add(1, 'day').set({hour: 0, minute: 0, second: 0});

  let refreshTime = laterTime.diff(nowTime);

  // console.log(nowTime.format('YYYY-MM-DD HH:mm:ss'), laterTime.format('YYYY-MM-DD HH:mm:ss'), refreshTime);
    
  // console.log(laterTime.diff(nowTime, 'seconds'));

  refreshInterval.value = setTimeout(() => {
    location.reload();
}, refreshTime);

});

// 사이트/설비 변경 시 모든 API 재시작
watch(
  () => [tocStore.selectSiteInfo, equipStore.selectEquipInfo],
  async () => {
    // 잠시 후 모든 API 재시작
    setTimeout(() => {
      if (isAllApiEnabled.value) {
        baseComponentRefs.value.forEach((ref) => {
          if (ref && ref.callApi) {
            ref.callApi();
          }
        });
      }
    }, 100);
  },
  { deep: true }
);


// 컴포넌트 언마운트 시 정리
onUnmounted(() => {
  baseComponentRefs.value.clear();
  clearInterval(refreshInterval.value);
});

// 위젯 ID 생성 함수
const generateWidgetId = (widget: any, child?: any) => {
  if (child) {
    return `${widget.type}_${child.type}_${child.data?.url || 'unknown'}`;
  }
  return `${widget.type}_${widget.data?.url || 'unknown'}`;
};

// 그리드 클래스 생성 함수
// 12단계: col-1 ~ col-12 (Bootstrap 기본)
// 24단계: col-24-1 ~ col-24-24
// 36단계: col-36-1 ~ col-36-36
function getGridClass(w: number) {
  if (gridColumns.value === 12) {
    return `col-${w}`;
  } else {
    return `col-${gridColumns.value}-${w}`;
  }
}

// 위치 계산 함수 (left 스타일 값)
function getLeftPosition(x: number) {
  return `${(100 / gridColumns.value) * x}%`;
}

// MapRender API 파라미터 변경 핸들러
const handleMapRenderApiParamsChanged = (params: { parentDongCode?: string | null, groupLevel?: string | null }) => {
  console.log('🔄 MapRender API 파라미터 변경됨:', params);
  mapRenderParams.value = params;
  
  // 에너지 현황 ProdChart들에 동적 파라미터 전달 확인
  console.log('📊 에너지 현황 ProdChart에 전달될 파라미터:', mapRenderParams.value);
};

// 대시보드 타입 체크: equip-geo-new인지 확인
const isEquipGeoNew = computed(() => {
  if (!props.widgets || props.widgets.length === 0) {
    return false;
  }
  
  // widgets 배열에 EditableImageCard가 있는지 확인
  return props.widgets.some((widget: any) => {
    // widgetBase 타입인 경우 child 배열 확인
    if (widget.type === 'widgetBase' && widget.child) {
      return widget.child.some((child: any) => child.type === 'EditableImageCard');
    }
    // 단일 위젯인 경우 직접 확인
    return widget.type === 'EditableImageCard';
  });
});

// 권한 체크 computed
const canEditImage = computed(() => {
  // equip-geo-new 대시보드인지 확인
  if (!isEquipGeoNew.value) {
    return false;
  }
  
  // 권한 체크 (UserAuthGroup: 1, 3, 4, 5, 6)
  const authGroupSeq = userStore.loginUserInfo?.authGroupSeq;
  const allowedGroups = [UserAuthGroup.Admin, UserAuthGroup.Contractor, UserAuthGroup.RtuManufact, UserAuthGroup.SafetyManager, UserAuthGroup.HostBizOperator];
  return authGroupSeq && allowedGroups.includes(authGroupSeq);
});

// EditableImageCard 메뉴 제어 상태
const showImageMenu = ref(false);
const imageMenuPosition = ref({ top: 0, left: 0 });

// 설정 아이콘 클릭 핸들러
function handleSettingsClick(event: MouseEvent) {
  if (!canEditImage.value) return;
  
  const target = event.currentTarget as HTMLElement;
  const rect = target.getBoundingClientRect();
  imageMenuPosition.value = {
    top: rect.bottom + 5,
    left: rect.right - 150 // 메뉴 너비 고려
  };
  showImageMenu.value = !showImageMenu.value;
}

// 메뉴 닫기 핸들러
function handleCloseMenu() {
  showImageMenu.value = false;
}

</script>

<template>
  <div class="row h-100" :style="{position: 'relative'}">
    <template v-for="(widget, i) in widgets">
      <!-- 위젯 기본 타입 -->
      <div v-if="widget.type === 'widgetBase'" 
          class="p-1"
          :class="`${getGridClass(widget.w)} h-${widget.h}`"
          :style="{
            position: 'absolute',
            left: getLeftPosition(widget.x),
            top:  `${widget.y}%`,
          }"  
        >
        <div class="in-wrapper h-100" v-if="widget.property.pageType !== 'tabs'">
          <div class="equip-header common-card" v-if="widget.property.pageType === 'equip'">
            <div class="common-card__title d-flex align-items-center justify-content-between">
              <h2>
                {{ commonUtils.isAllStrDataEmpty(equipStore.selectEquipInfo.equipName) }}
              </h2>
              <div class="d-flex align-items-center gap-2">
                <div class="sub">
                  설비번호 - {{ commonUtils.isAllStrDataEmpty(equipStore.selectEquipInfo.cid) }}
                </div>
            
              </div>
            </div>
          </div>
          <div 
            v-else-if="widget.property.geoType === 'drgeo'"
            class="equip-header common-card"
          >
          <div class="common-card__title d-flex align-items-center justify-content-between">
              <h2>
                {{ commonUtils.isAllStrDataEmpty(equipStore.selectEquipInfo.siteName) }}
              </h2>
              <div class="d-flex align-items-center gap-2">
                  <!-- 설정 아이콘 추가 -->
                  <button 
                  v-if="canEditImage"
                  class="btn btn-sm btn-link p-0"
                  @click="handleSettingsClick"
                  type="button"
                  title="이미지 설정"
                >
                  <i class="bi bi-gear-fill" style="font-size: 1.2rem; color: #007CE1;"></i>
                </button>
              </div>
            </div>
          </div>
          <div 
            v-else-if="widget.property.pageType === 'site'"
            class="site-header common-card p-0" 
          >
            <div class="common-card__title m-0 h-100 d-flex align-items-center">
              <h2 class="main">{{ commonUtils.isAllStrDataEmpty(tocStore.selectSiteInfo.siteName) }}</h2>
              <h2 class="sub">{{ `${commonUtils.isAllStrDataEmpty(tocStore.selectSiteInfo.equipAddr)}` }}</h2>

             
            </div>
          </div>
          <div
            v-for="(child, j) in widget.child"
            :key="`${i}_${j}`"
            :class="`${getGridClass(child.w)} h-${child.h}`"
            :style="{
            position: 'absolute',
            left: getLeftPosition(child.x),
            top:  `${child.y}%`,
          }"
          >
          <!-- GeothermalProdUsageChart는 BaseComponent를 사용하지 않고 직접 처리 -->
          <GeothermalProdUsageChart 
            v-if="child.type === 'GeothermalProdUsageChart'"
            :data="child.data"
            :property="child.property"
            :chartParams="child.chartParams"
            :intervalTime="child.intervalTime"
            :pageType="props.pageType"
          />
          
          <!-- BaseComponent를 사용하여 API 호출 제어 -->
          <BaseComponent 
            v-else-if="child.type !== 'InfoTap' && child.data"
            :api="child.data" 
            :pageType="props.pageType" 
            :property="child.property"
            :intervalTime="child.intervalTime"
            :dynamicParams="child.type === 'ProdChart' || child.type === 'TotalProdCard' ? mapRenderParams : undefined"
          >
            <template v-slot:default="slotProps">
              <!-- 로딩 상태 표시 -->
              <div v-if="slotProps.isLoading && !slotProps.hasInitialData" class="component-loading">
                <div class="spinner-border spinner-border-sm text-primary" role="status">
                  <span class="visually-hidden">Loading...</span>
                </div>
                <small class="text-muted ms-2">데이터 로딩 중...</small>
              </div>
              
              <!-- 실제 컴포넌트 렌더링 -->
              <template v-else>
                <BasicInfoCard v-if="child.type === 'BasicInfoCard'" :apiData="slotProps.apiData"></BasicInfoCard>
                <BasicValueCard v-else-if="child.type === 'BasicValueCard'" :apiData="slotProps.apiData" :property="child.property" :style="child.style"></BasicValueCard>
                <EnergyInfoCard v-else-if="child.type === 'EnergyInfoCard'" :apiData="slotProps.apiData" :property="child.property" :style="child.style"></EnergyInfoCard>
                <EventTable v-else-if="child.type === 'EventTable'" :apiData="slotProps.apiData" :property="child.property" :isStatusTab="false" @callApi="slotProps.onCallApi"></EventTable>
                <InvertCard v-else-if="child.type === 'InvertCard'" :apiData="slotProps.apiData"></InvertCard>
                <InvertCardList v-else-if="child.type === 'InvertCardList'" :apiData="slotProps.apiData" :property="child.property"></InvertCardList>
                <LinkageInfoCard v-else-if="child.type === 'LinkageInfoCard'" :apiData="slotProps.apiData" :type="child.property.type"></LinkageInfoCard>
                <ProdChart v-else-if="child.type === 'ProdChart'"
                  :apiData="slotProps.apiData"
                  :property="child.property"
                  :chartParams="child.chartParams"
                  :style="child.style"
                />
                <DateChart v-else-if="child.type === 'DateChart'" :apiData="slotProps.apiData" :property="child.property" :style="child.style"></DateChart>
                <RoundChart v-else-if="child.type === 'RoundChart'" :apiData="slotProps.apiData"></RoundChart>
                <SchematicCard v-else-if="child.type === 'SchematicCard'" :apiData="slotProps.apiData"></SchematicCard>
                <TotalProdCard v-else-if="child.type === 'TotalProdCard'" :apiData="slotProps.apiData" :style="child.style" :property="child.property"></TotalProdCard>
                <WeatherInfo v-else-if="child.type === 'WeatherInfo'" :apiData="slotProps.apiData"></WeatherInfo>
                <WeatherProdChart v-else-if="child.type === 'WeatherProdChart'" :apiData="slotProps.apiData"></WeatherProdChart>
                <GeothermalSchematicCard v-else-if="child.type === 'GeothermalSchematicCard'" :apiData="slotProps.apiData"></GeothermalSchematicCard>
                <EditableImageCard 
                  v-else-if="child.type === 'EditableImageCard'" 
                  :apiData="slotProps.apiData" 
                  :property="child.property"
                  :showMenu="showImageMenu"
                  :menuPosition="imageMenuPosition"
                  @closeMenu="handleCloseMenu"
                ></EditableImageCard>
                <SystemStatusCard v-else-if="child.type === 'SystemStatusCard'" :apiData="slotProps.apiData" :property="child.property"  @callApi="slotProps.onCallApi"></SystemStatusCard>
                <BaseTable
                  v-else-if="child.type === 'BaseTable'"
                  :apiData="slotProps.apiData"
                  :property="child.property"
                  :callApiFn="slotProps.onCallApi"
                />
                <GaugeChart v-else-if="child.type === 'gaugeChart'" :value="30" :title="'타이틀 입니다.'"></GaugeChart>
                <CurtailmentCard v-else-if="child.type === 'curtailment'"></CurtailmentCard>
              </template>
            </template>
          </BaseComponent>
          <InfoTap v-else-if="child.type === 'InfoTap'" :property="child.property" :widget="child"></InfoTap>
        </div>
      </div>
        <div class="in-wrapper h-100" v-else>
          <div class="h-100">
            <div class="col-12 h-10">
              <div class="tab-area common-card">
                <div class="common-card__title m-0">
                  <h2 style="min-height: unset">
                    에너지 현황
                  </h2>
                </div>
                <div class="tab-list">
                  <ul class="nav nav-pills" id="pills-tab" role="tablist">
                    <li class="nav-item" role="presentation" v-for="(tab, tabIndex) in widget.tabs">
                      <button :class="tabIndex === 0 ? `active nav-link ${tab.tabid}` : `nav-link ${tab.tabid}`" :id="`pills-${tab.tabid}-tab`" data-bs-toggle="pill" :data-bs-target="`#pills-${tab.tabid}`" type="button" role="tab" :aria-controls="`pills-${tab.tabid}`" aria-selected="true">
                        {{ tab.name }}
                      </button>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
              <div class="content-area ">
                <div class="tab-content " id="pills-tabContent">
                  <div v-for="(tab, tabIndex) in widget.tabs" class="tab-pane fade h-100" :class="tabIndex === 0 ? 'show active' : ''" :id="`pills-${tab.tabid}`" role="tabpanel" :aria-labelledby="`pills-${tab.tabid}-tab`" :tabindex="`${tabIndex}`">
                    <div
                        v-for="(child, childIndex) in tab.child"
                        :key="`tab_${tabIndex}_child_${childIndex}`"
                        :class="`${getGridClass(child.w)} h-${child.h}`"
                        :style="{
                        position: 'absolute',
                        left: getLeftPosition(child.x),
                        top:  `${child.y}%`,
                      }"
                      >
                      <!-- 탭 내부 컴포넌트도 BaseComponent 사용 -->
                      <BaseComponent 
                        v-if="child.type !== 'InfoTap' && child.data"
                        :api="child.data" 
                        :pageType="props.pageType" 
                        :property="child.property"
                        :intervalTime="child.intervalTime"
                        :dynamicParams="child.type === 'ProdChart' || child.type === 'TotalProdCard' ? mapRenderParams : undefined"
                      >
                        <template v-slot:default="slotProps">
                          <!-- 로딩 상태 표시 -->
                          <div v-if="slotProps.isLoading && !slotProps.hasInitialData" class="component-loading">
                            <div class="spinner-border spinner-border-sm text-primary" role="status">
                              <span class="visually-hidden">Loading...</span>
                            </div>
                            <small class="text-muted ms-2">데이터 로딩 중...</small>
                          </div>
                          
                          <!-- 실제 컴포넌트 렌더링 -->
                          <template v-else>
                            <BasicInfoCard v-if="child.type === 'BasicInfoCard'" :apiData="slotProps.apiData"></BasicInfoCard>
                            <BasicValueCard v-else-if="child.type === 'BasicValueCard'" :apiData="slotProps.apiData" :property="child.property" :style="child.style"></BasicValueCard>
                            <EnergyInfoCard v-else-if="child.type === 'EnergyInfoCard'" :apiData="slotProps.apiData" :property="child.property" :style="child.style"></EnergyInfoCard>
                            <EventTable v-else-if="child.type === 'EventTable'" :apiData="slotProps.apiData" :property="child.property" :isStatusTab="false" @callApi="slotProps.onCallApi"></EventTable>
                            <InvertCard v-else-if="child.type === 'InvertCard'" :apiData="slotProps.apiData"></InvertCard>
                            <InvertCardList v-else-if="child.type === 'InvertCardList'" :apiData="slotProps.apiData" :property="child.property"></InvertCardList>
                            <LinkageInfoCard v-else-if="child.type === 'LinkageInfoCard'" :apiData="slotProps.apiData" :type="child.property.type"></LinkageInfoCard>
                            <ProdChart v-else-if="child.type === 'ProdChart'"
                              :apiData="slotProps.apiData"
                              :property="child.property"
                              :chartParams="child.chartParams"
                              :style="child.style"
                            />
                            <DateChart v-else-if="child.type === 'DateChart'" :apiData="slotProps.apiData" :property="child.property" :style="child.style"></DateChart>
                            <RoundChart v-else-if="child.type === 'RoundChart'" :apiData="slotProps.apiData"></RoundChart>
                            <SchematicCard v-else-if="child.type === 'SchematicCard'" :apiData="slotProps.apiData"></SchematicCard>
                            <TotalProdCard v-else-if="child.type === 'TotalProdCard'" :apiData="slotProps.apiData" :style="child.style" :property="child.property"></TotalProdCard>
                            <WeatherInfo v-else-if="child.type === 'WeatherInfo'" :apiData="slotProps.apiData"></WeatherInfo>
                            <WeatherProdChart v-else-if="child.type === 'WeatherProdChart'" :apiData="slotProps.apiData"></WeatherProdChart>
                            <SystemStatusCard v-else-if="child.type === 'SystemStatusCard'" :apiData="slotProps.apiData" :property="child.property" @callApi="slotProps.onCallApi"></SystemStatusCard>
                            <BaseTable
                              v-else-if="child.type === 'BaseTable'"
                              :apiData="slotProps.apiData"
                              :property="child.property"
                              :callApiFn="child.property?.syncWithRange ? slotProps.onCallApi : undefined"
                            />
                            <GaugeChart v-else-if="child.type === 'gaugeChart'" :value="30" :title="'타이틀 입니다.'"></GaugeChart>
                            <CurtailmentCard v-else-if="child.type === 'curtailment'"></CurtailmentCard>
                          </template>
                        </template>
                      </BaseComponent>
                      
                      <InfoTap v-else-if="child.type === 'InfoTap'" :property="child.property" :widget="child"></InfoTap>
                    </div>
                  </div>
                </div>
              </div>
            </div>
        </div>
      </div>
      <!-- 단일 위젯 타입 -->
      <div
        v-else 
        :class="`${getGridClass(widget.w)} p-1 h-${widget.h}`"
        :style="{
          position: 'absolute',
          left: getLeftPosition(widget.x),
          top:  `${widget.y}%`,
        }"
        >
        <!-- GeothermalProdUsageChart는 BaseComponent를 사용하지 않고 직접 처리 -->
        <GeothermalProdUsageChart 
          v-if="widget.type === 'GeothermalProdUsageChart'"
          :data="widget.data"
          :property="widget.property"
          :chartParams="widget.chartParams"
          :intervalTime="widget.intervalTime"
          :pageType="props.pageType"
        />
        
        <!-- 단일 위젯도 BaseComponent 사용 -->
        <BaseComponent 
          v-else-if="widget.type !== 'InfoTap' && widget.data"
          :api="widget.data" 
          :pageType="props.pageType" 
          :intervalTime="widget.intervalTime"
          :property="widget.type"
          :dynamicParams="widget.type === 'MapRender' || widget.type === 'ProdChart' || widget.type === 'TotalProdCard' ? mapRenderParams : undefined"
        >
          <template v-slot:default="slotProps">
            <!-- 로딩 상태 표시 -->
            <div v-if="slotProps.isLoading && !slotProps.hasInitialData" class="component-loading h-100">
              <div class="spinner-border spinner-border-sm text-primary" role="status">
                <span class="visually-hidden">Loading...</span>
              </div>
              <small class="text-muted ms-2">데이터 로딩 중...</small>
            </div>
            
            <!-- 실제 컴포넌트 렌더링 -->
            <template v-else>
              <BasicInfoCard v-if="widget.type === 'BasicInfoCard'" :apiData="slotProps.apiData" />
              <BasicValueCard v-else-if="widget.type === 'BasicValueCard'" :apiData="slotProps.apiData" :property="widget.property" :style="widget.style" />
              <EnergyInfoCard v-else-if="widget.type === 'EnergyInfoCard'" :apiData="slotProps.apiData" :property="widget.property" :style="widget.style" />
              <EventTable v-else-if="widget.type === 'EventTable'" :apiData="slotProps.apiData" :property="widget.property" :isStatusTab="widget.property.isStatus ?? false" @callApi="slotProps.onCallApi" />
              <InvertCard v-else-if="widget.type === 'InvertCard'" :apiData="slotProps.apiData" />
              <InvertCardList v-else-if="widget.type === 'InvertCardList'" :apiData="slotProps.apiData" :property="widget.property" />
              <LinkageInfoCard v-else-if="widget.type === 'LinkageInfoCard'" :apiData="slotProps.apiData" :type="widget.property.type" />
              <ProdChart v-else-if="widget.type === 'ProdChart'"
                :apiData="slotProps.apiData"
                :property="widget.property"
                :chartParams="widget.chartParams"
                :style="widget.style"
              />
              <DateChart v-else-if="widget.type === 'DateChart'" :apiData="slotProps.apiData" :property="widget.property" :style="widget.style"></DateChart>
              <RawdataCard v-else-if="widget.type === 'RawDataCard'" :apiData="slotProps.apiData" @callAPI="slotProps.onCallApi"/>
              <RoundChart v-else-if="widget.type === 'RoundChart'" :apiData="slotProps.apiData" />
              <SchematicCard v-else-if="widget.type === 'SchematicCard'" :apiData="slotProps.apiData" />
              <TotalProdCard v-else-if="widget.type === 'TotalProdCard'" :apiData="slotProps.apiData" :style="widget.style" :property="widget.property" />
              <WeatherInfo v-else-if="widget.type === 'WeatherInfo'" :apiData="slotProps.apiData" />
              <WeatherProdChart v-else-if="widget.type === 'WeatherProdChart'" :apiData="slotProps.apiData" />
              <GeothermalSchematicCard v-else-if="widget.type === 'GeothermalSchematicCard'" :apiData="slotProps.apiData" />
              <EditableImageCard 
                v-else-if="widget.type === 'EditableImageCard'" 
                :apiData="slotProps.apiData" 
                :property="widget.property"
                :showMenu="showImageMenu"
                :menuPosition="imageMenuPosition"
                @closeMenu="handleCloseMenu"
              />
              <SystemStatusCard v-else-if="widget.type === 'SystemStatusCard'" :apiData="slotProps.apiData" :property="widget.property" @callApi="slotProps.onCallApi" />
              <BaseTable
                v-else-if="widget.type === 'BaseTable'"
                :apiData="slotProps.apiData"
                :property="widget.property"
                :callApiFn="widget.property?.syncWithRange ? slotProps.onCallApi : undefined"
              />
              <GaugeChart v-else-if="widget.type === 'gaugeChart'" :value="30" :title="'타이틀 입니다.'"></GaugeChart>
              <CurtailmentCard v-else-if="widget.type === 'curtailment'"></CurtailmentCard>
              <MapRender 
                v-else-if="widget.type === 'MapRender'" 
                :apiData="slotProps.apiData"
                @apiParamsChanged="handleMapRenderApiParamsChanged"
              ></MapRender>
            </template>
          </template>
        </BaseComponent>
        
        <InfoTap v-else-if="widget.type === 'InfoTap'" :property="widget.property" :widget="widget"></InfoTap>
      </div>
    </template>
  </div>
</template>

<style lang="scss" scoped>
@use "@/assets/styles/design-toc/define_font.scss";
@use "@/assets/styles/design-toc/common-card.scss";

.in-wrapper {
  position: relative;
  border-radius: 10px;
}

.equip-header {
  height: 50px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem 0.5rem;
  border-radius: 10px 10px 0 0;

  & .title {
    @include define_font.text-bold-16;
    white-space: nowrap;
  }
  & .sub {
    @include define_font.text-normal-14;
    color: #4E4E4E;
    text-align: right;
    font-weight: 500;
  }

  & :deep(.common-card__title) {
    width: 100%;
    padding: 0 0.5rem;
    margin: 0;

    h2 {
      min-height: unset;
    }
  }

  .btn-link {
    &:hover {
      opacity: 0.7;
    }
    
    &:focus {
      box-shadow: none;
    }
  }
}
.site-header {
  height: 40px;

  .title{
    padding: 10px;
    display: flex;
    justify-content: space-between;
    align-items: center;
    height: 100%;
    padding: 10px 20px;

    // .main {
    //   @include define_font.text-bold-18;
    //   font-weight: 700;
    //   margin: 0;
    // }
    // .sub {
    //   @include define_font.text-normal-14;
    //   font-weight: 400;
    //   line-height: 16px; /* 114.286% */
    //   margin: 0;
    // }
  }
  & :deep(.common-card__title) {
    padding: 0 1rem;

    h2 {
      min-height: unset;
    }
  }
}


.tab-area{
  border-radius: 10px;
  background: #FFF;  
  padding: 7px 10px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  .title{
    color: #262626;
    font-family: "Noto Sans";
    font-size: 22px;
    font-style: normal;
    font-weight: 700;
    line-height: 16px; /* 66.667% */
  }
  .tab-list{
    .nav{
      .nav-item{
        button{
          text-align: right;
          font-feature-settings: 'liga' off, 'clig' off;
          /* Display/3/Regular */
          font-family: Inter;
          font-size: 14px;
          font-style: normal;
          font-weight: 500;
          line-height: 20px; /* 142.857% */
          color: #000;
          &.active{
            color: #fff
          }          

          &.usage.active{
            background: #F27121;
          }
        }
      }
    }
  }
}
:deep(.col-15) {
  width: 12.5%;
}

// 24단계 그리드 시스템
@for $i from 1 through 24 {
  :deep(.col-24-#{$i}) {
    width: calc(100% / 24 * #{$i});
    flex: 0 0 calc(100% / 24 * #{$i});
    max-width: calc(100% / 24 * #{$i});
  }
}

// 36단계 그리드 시스템
@for $i from 1 through 36 {
  :deep(.col-36-#{$i}) {
    width: calc(100% / 36 * #{$i});
    flex: 0 0 calc(100% / 36 * #{$i});
    max-width: calc(100% / 36 * #{$i});
  }
}

.component-loading {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem;
  background: rgba(0, 0, 0, 0.05);
  border-radius: 5px;
  min-height: 60px;
}
</style>